// Logistics → Carriers mode panel orchestrator (D3f).
// Primary action: + New carrier.
//
// Sister surface to LogisticsRoutesPanel in the same Logistics bucket.
// Backend lives in D3c (logi_carriers create/list/update); this panel
// is the admin face -- create new carriers, edit details, toggle
// active. The Routes create-form picker uses the same backend with
// is_active=true so deactivating a carrier here hides it from the
// route-creation picker without breaking historical route references.
//
// Same "K-doctrine" shape as ManufacturingPlantsPanel and
// LogisticsRoutesPanel: wrapper div with header + inline action
// buttons, gate banner, local-error block, then the SectionCard
// composition under the gate.
import { useCallback, useState } from "react";
import { LogisticsGateBanner } from "./LogisticsGateBanner";
import { useLogisticsAnchorLayerGate } from "./useLogisticsAnchorLayerGate";
import { LogisticsCarriersCreateForm } from "./LogisticsCarriersCreateForm";
import { LogisticsCarriersList } from "./LogisticsCarriersList";
import { ghostBtnClass, primaryBtnClass } from "./logisticsCarriersShared";
import { useLogisticsCarriersPanel } from "./useLogisticsCarriersPanel";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export function LogisticsCarriersPanel() {
  const gate = useLogisticsAnchorLayerGate();
  const [navTick, setNavTick] = useState(0);
  const [newSignal, setNewSignal] = useState(0);
  const m = useLogisticsCarriersPanel(navTick, newSignal);

  const handleRefresh = useCallback(() => {
    gate.refresh();
    setNavTick((s) => s + 1);
  }, [gate]);

  return (
    <div
      {...(gate.enabled ? { [FIREFLY_TARGET_ATTR]: "logi-carriers-surface" } : {})}
      className="w-full text-left space-y-4"
    >
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Carriers</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            {...{ [FIREFLY_TARGET_ATTR]: "logi-carriers-new" }}
            className={primaryBtnClass}
            onClick={() => setNewSignal((s) => s + 1)}
            disabled={!gate.enabled || gate.loading}
          >
            + New carrier
          </button>
          <button
            type="button"
            className={ghostBtnClass}
            onClick={handleRefresh}
            disabled={gate.loading || m.listBusy || m.actionBusy}
          >
            {m.listBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <LogisticsGateBanner gate={gate} />
      {gate.enabled && m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}

      {gate.enabled ? (
        <>
          <LogisticsCarriersCreateForm m={m} />
          <LogisticsCarriersList m={m} />
        </>
      ) : null}
    </div>
  );
}
